import React, { useState, useEffect, useCallback } from 'react';
import { getRefundRequestsApi, approveRefundRequestApi, rejectRefundRequestApi } from '../api/adminBilling.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import PageHeader from '../components/PageHeader';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';

export default function RefundManagement() {
  const [refunds, setRefunds] = useState([]);
  const [statusFilter, setStatusFilter] = useState(''); // '' means all
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  
  // Rejections state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Approvals state
  const [showApproveModal, setShowApproveModal] = useState(false);

  const {
    page,
    limit,
    total,
    loading,
    setLoading,
    resetPage,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(10);

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchRefundRequests = useCallback(async (pageNum = page) => {
    setLoading(true);
    setError(null);
    const params = { page: pageNum, limit };
    if (statusFilter) {
      params.status = statusFilter;
    }
    
    const result = await apiHandler(() => getRefundRequestsApi(params));
    if (result.success) {
      const data = unwrapData(result);
      setRefunds(data.refundRequests || []);
      applyPaginationMeta(data);
    } else {
      setError(result.message || 'Failed to load refund requests');
    }
    setLoading(false);
  }, [statusFilter, page, limit, applyPaginationMeta, setLoading]);

  useEffect(() => {
    fetchRefundRequests(page);
  }, [page, statusFilter]);

  const handleFilterChange = (status) => {
    setStatusFilter(status);
    resetPage();
  };

  const openApproveModal = (refund) => {
    setSelectedRefund(refund);
    setShowApproveModal(true);
  };

  const executeApproval = async () => {
    if (!selectedRefund) return;
    setActioningId(selectedRefund.id);
    setShowApproveModal(false);
    
    const result = await apiHandler(() => approveRefundRequestApi(selectedRefund.id));
    if (result.success) {
      showToast('Refund approved and processed successfully via Stripe.', 'success');
      fetchRefundRequests(page);
    } else {
      showToast(result.message || 'Failed to approve refund', 'error');
    }
    setActioningId(null);
    setSelectedRefund(null);
  };

  const openRejectModal = (refund) => {
    setSelectedRefund(refund);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const executeRejection = async (e) => {
    e.preventDefault();
    if (!selectedRefund) return;
    
    setActioningId(selectedRefund.id);
    setShowRejectModal(false);

    const payload = { rejectionReason: rejectionReason || 'Rejected by administrator' };
    const result = await apiHandler(() => rejectRefundRequestApi(selectedRefund.id, payload));
    if (result.success) {
      showToast('Refund request rejected successfully.', 'success');
      fetchRefundRequests(page);
    } else {
      showToast(result.message || 'Failed to reject refund request', 'error');
    }
    setActioningId(null);
    setSelectedRefund(null);
  };

  return (
    <>
      {toast && (
        <div
          className="fixed top-20 right-5 z-[99999] px-5 py-3 rounded-xl shadow-md border text-sm font-semibold animate-slide-in"
          style={{
            background: toast.type === 'success' ? '#F0FDF4' : toast.type === 'error' ? '#FEF2F2' : '#EFF6FF',
            borderColor: toast.type === 'success' ? '#BBF7D0' : toast.type === 'error' ? '#FCA5A5' : '#BFDBFE',
            color: toast.type === 'success' ? '#15803D' : toast.type === 'error' ? '#B91C1C' : '#1D4ED8',
          }}
        >
          {toast.msg}
        </div>
      )}

      <PageHeader
        title="Refund Management"
        icon="fa-hand-holding-dollar"
        trail={[{ label: 'Billing' }, { label: 'Refunds' }]}
      />

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 mb-4 text-xs text-rose-700 font-medium">
          {error}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex border-b border-slate-100 mb-5 gap-1.5 flex-wrap">
        {[
          { label: 'All Requests', value: '' },
          { label: 'Pending Approval', value: 'pending' },
          { label: 'Approved Refunds', value: 'approved' },
          { label: 'Rejected', value: 'rejected' },
        ].map((tab) => (
          <button
            key={tab.label}
            onClick={() => handleFilterChange(tab.value)}
            className={`px-4 py-2 text-xs font-bold transition-all border-b-2 -mb-px ${
              statusFilter === tab.value
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="section-card">
        <div className="section-header border-b pb-3 mb-4">
          <div className="section-title text-sm font-bold text-slate-800 flex items-center gap-1.5">
            <i className="fas fa-list" />
            Refund Queue
            <span className="bg-slate-100 text-slate-500 rounded-full px-2 py-0.5 text-[10px] font-semibold">
              {loading ? '…' : `${total} requests`}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="data-table min-w-[900px] w-full text-xs text-slate-700">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 font-semibold">
                <th className="p-3 text-left">Request Date</th>
                <th className="p-3 text-left">User</th>
                <th className="p-3 text-left">Original Payment</th>
                <th className="p-3 text-left">Refund Request</th>
                <th className="p-3 text-left">Reason / Rejection Details</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center p-6 text-slate-400">Loading requests queue...</td>
                </tr>
              ) : refunds.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center p-6 text-slate-400">No matching refund requests found.</td>
                </tr>
              ) : (
                refunds.map((refund) => {
                  const tx = refund.transaction || {};
                  return (
                    <tr key={refund.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-slate-500">
                        {new Date(refund.createdAt).toLocaleDateString()}<br />
                        <span className="text-[10px] font-mono text-slate-400">{refund.id.slice(0, 8)}</span>
                      </td>
                      <td className="p-3">
                        <div className="font-bold text-slate-800">
                          {refund.user ? `${refund.user.firstName || ''} ${refund.user.lastName || ''}`.trim() : 'Anonymous'}
                        </div>
                        <div className="text-[10px] text-slate-400">{refund.user?.email || 'N/A'}</div>
                      </td>
                      <td className="p-3 text-slate-600">
                        Paid: <strong>${tx.amount || '0'}</strong><br />
                        <span className="text-[10px] font-mono text-slate-400">PI: {tx.stripePaymentIntentId?.slice(0, 12)}...</span>
                      </td>
                      <td className="p-3">
                        <span className="font-extrabold text-rose-600">${refund.amount}</span>
                      </td>
                      <td className="p-3 max-w-[240px] truncate-2-lines text-slate-600" title={refund.reason || refund.rejectionReason}>
                        {refund.reason && <span>📝 {refund.reason}</span>}
                        {refund.rejectionReason && (
                          <span className="block text-rose-500 text-[10px] font-medium mt-1">
                            ❌ Rejection: {refund.rejectionReason}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                          refund.status === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                          refund.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-200' :
                          'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}>
                          {refund.status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {refund.status === 'pending' ? (
                          <div className="flex gap-2 justify-end">
                            <button
                              disabled={actioningId === refund.id}
                              onClick={() => openApproveModal(refund)}
                              className="bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 font-bold px-2 py-1 rounded text-[10px] transition-all"
                            >
                              Approve
                            </button>
                            <button
                              disabled={actioningId === refund.id}
                              onClick={() => openRejectModal(refund)}
                              className="bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100 font-bold px-2 py-1 rounded text-[10px] transition-all"
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">Reviewed</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination {...paginationProps} />

      {/* Approve Modal */}
      {showApproveModal && selectedRefund && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[9999] p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 border border-slate-100 shadow-xl flex flex-col gap-4 text-center">
            <div className="w-12 h-12 bg-green-50 border border-green-200 text-green-600 rounded-full flex items-center justify-center mx-auto text-xl">
              <i className="fas fa-check" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-bold text-slate-800 text-base">Approve Refund Request?</span>
              <p className="text-xs text-slate-500">
                This will trigger a live transaction refund of <strong className="text-rose-600">${selectedRefund.amount}</strong> to the user's payment card via the Stripe API. This cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 mt-2 justify-center">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 border rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50"
              >
                No, Keep Pending
              </button>
              <button
                onClick={executeApproval}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700"
              >
                Yes, Approve & Execute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRefund && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[9999] p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 border border-slate-100 shadow-xl flex flex-col gap-4">
            <span className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
              <i className="fas fa-times text-rose-600" />
              Reject Refund Request
            </span>
            <form onSubmit={executeRejection} className="flex flex-col gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Reason for Rejection</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter rejection details that will be saved in history..."
                  className="w-full border border-slate-200 rounded-lg p-2 text-xs text-slate-800 h-20"
                  required
                />
              </div>
              <div className="flex gap-3 justify-end border-t pt-3 mt-1">
                <button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  className="px-3 py-1.5 border rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700"
                >
                  Confirm Rejection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
